import React from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import './ClothedEyeError.css';

/**
 * Unified ClothedEye Error Component
 *
 * Features:
 * - Logo text at top with orange color
 * - BMDITS tagline below in white
 * - Error icon/indicator centered
 * - Error title and description
 * - Action buttons (Try Again, Go Home, etc.)
 * - Consistent with ClothedEyeLoader design
 * - Responsive design
 */
const ClothedEyeError = ({
  errorType = 'generic', // 'generic', '404', '500', '403', 'network', 'boundary'
  title = null, // Custom error title
  description = null, // Custom error description
  onRetry = null, // Retry callback
  onGoHome = null, // Go home callback
  showIcon = true, // Show error icon
  size = 'medium', // 'small', 'medium', 'large'
  className = '', // Additional CSS classes
  theme = 'auto', // 'auto', 'dark', 'light'
  fullscreen = false, // Whether to use full-screen styling
}) => {
  // Error configurations for different types
  const errorConfigs = {
    generic: {
      icon: 'error_outline',
      title: 'Something went wrong',
      description: 'An unexpected error occurred. Please try again.',
      showRetry: true,
      showHome: true,
    },
    404: {
      icon: 'search_off',
      title: 'Page Not Found',
      description: "The page you're looking for doesn't exist or has been moved.",
      showRetry: false,
      showHome: true,
    },
    500: {
      icon: 'warning',
      title: 'Server Error',
      description: "Our servers encountered an error. We're working to fix it.",
      showRetry: true,
      showHome: true,
    },
    403: {
      icon: 'lock',
      title: 'Access Forbidden',
      description: "You don't have permission to access this resource.",
      showRetry: false,
      showHome: true,
    },
    network: {
      icon: 'wifi_off',
      title: 'Connection Error',
      description: 'Unable to connect to the server. Please check your internet connection.',
      showRetry: true,
      showHome: false,
    },
    boundary: {
      icon: 'broken_image',
      title: 'Component Error',
      description: 'A component crashed unexpectedly. Please refresh the page.',
      showRetry: true,
      showHome: true,
    },
  };

  const config = errorConfigs[errorType] || errorConfigs.generic;

  const sizeClasses = {
    small: 'clothedeye-error--small',
    medium: 'clothedeye-error--medium',
    large: 'clothedeye-error--large',
  };

  const themeClasses = {
    auto: 'clothedeye-error--auto',
    dark: 'clothedeye-error--dark',
    light: 'clothedeye-error--light',
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Default retry behavior: reload the page
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      // Default go home behavior: navigate to root
      window.location.href = '/';
    }
  };

  return (
    <div
      className={`clothedeye-error ${sizeClasses[size]} ${themeClasses[theme]} ${fullscreen ? 'clothedeye-error--fullscreen' : ''} ${className}`}
    >
      {/* Full logo (gears) at top */}
      <div className="clothedeye-error__logo">
        <div className="clothedeye-error__logo-container">
          <img
            src="/assets/svgs/clothedEye-loader-spin.svg"
            alt=""
            className="clothedeye-error__logo-spin"
          />
          <img
            src="/assets/svgs/clothedEye-loader-static.svg"
            alt="ClothedEye"
            className="clothedeye-error__logo-static"
          />
        </div>
      </div>

      {/* BMDITS tagline below logo */}
      <div className="clothedeye-error__tagline">
        <img
          src="/assets/svgs/clothedEye_BMDITS.svg"
          alt="BMDITS"
          className="clothedeye-error__tagline-image"
        />
      </div>

      {/* Error icon centered */}
      {showIcon && (
        <div className="clothedeye-error__icon">
          <MaterialIcon icon={config.icon} className="clothedeye-error__icon-material" />
        </div>
      )}

      {/* Error title */}
      <h2 className="clothedeye-error__title">{title || config.title}</h2>

      {/* Error description */}
      <p className="clothedeye-error__description">{description || config.description}</p>

      {/* Action buttons */}
      <div className="clothedeye-error__actions">
        {config.showRetry && (
          <button
            onClick={handleRetry}
            className="clothedeye-error__button clothedeye-error__button--primary"
          >
            Try Again
          </button>
        )}
        {config.showHome && (
          <button
            onClick={handleGoHome}
            className="clothedeye-error__button clothedeye-error__button--secondary"
          >
            Go Home
          </button>
        )}
      </div>
    </div>
  );
};

ClothedEyeError.propTypes = {
  errorType: PropTypes.oneOf(['generic', '404', '500', '403', 'network', 'boundary']),
  title: PropTypes.string,
  description: PropTypes.string,
  onRetry: PropTypes.func,
  onGoHome: PropTypes.func,
  showIcon: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  className: PropTypes.string,
  theme: PropTypes.oneOf(['auto', 'dark', 'light']),
  fullscreen: PropTypes.bool,
};

export default ClothedEyeError;
